import { ProjectDBError } from './error';
import { GetProjectError, WriteFileError } from './fs/types';
import { OptTuple } from './typeUtils';
import { Commit, StatusMap, ReadObjectError } from './types';

export type TabIconType =
  | 'project'
  | 'file'
  | 'comparison'
  | 'rfq'
  | 'quote'
  | 'order'
  | 'project-file';

export type OpenTab = {
  title: string;
  iconType: TabIconType;
  navigation?: { routeName: string; options: any };
};

const gutFetch = (projectKey: string, downloadBlobs = false): Promise<void> => {
  return window.ipc?.invoke('gut-fetch', projectKey, downloadBlobs);
};

const gutMerge = (projectKey: string): Promise<void> => {
  return window.ipc?.invoke('gut-merge', projectKey);
};

const gutSyncBlobs = (
  projectKey: string,
  paths?: string[],
): Promise<OptTuple<Map<string, Error | null>, Error>> => {
  return window.ipc?.invoke('gut-syncBlobs', projectKey, paths);
};

const gutPull = (
  projectKey: string,
  downloadBlobs = true,
): Promise<OptTuple<boolean, Error>> => {
  return window.ipc?.invoke('gut-pull', projectKey, downloadBlobs);
};

const gutStatus = (projectKey: string): Promise<OptTuple<StatusMap, null>> => {
  return window.ipc?.invoke('gut-status', projectKey);
};

const gutReadCommit = (
  projectKey: string,
): Promise<OptTuple<Commit, GetProjectError | ReadObjectError>> => {
  return window.ipc?.invoke('gut-readCommit', projectKey);
};

const gutHeadHash = (projectKey: string): Promise<OptTuple<string, Error>> => {
  return window.ipc?.invoke('gut-getHeadHash', projectKey);
};

const gutConnect = (
  projectKey: string,
): Promise<[boolean, GetProjectError | WriteFileError | Error | null]> => {
  return window.ipc?.invoke('gut-connect', projectKey);
};

const gutIsConnected = (
  projectKey: string,
): Promise<[boolean, GetProjectError | null]> => {
  return window.ipc?.invoke('gut-isConnected', projectKey);
};

const gutGetConnectedPath = (
  projectKey: string,
): Promise<OptTuple<string, GetProjectError>> => {
  return window.ipc?.invoke('gut-getConnectedPath', projectKey);
};

const gutDisconnect = (projectKey: string): Promise<ProjectDBError | null> => {
  return window.ipc?.invoke('gut-disconnect', projectKey);
};

const gutResolve = (
  projectKey: string,
  to: string,
  paths: string[],
): Promise<OptTuple<any, Error>> => {
  return window.ipc?.invoke('gut-resolve', projectKey, to, paths);
};

const gutResetProject = (projectKey: string) => {
  return window.ipc?.invoke('gut-resetProject', projectKey);
};

const gutReset = (
  projectKey: string,
  paths: string[],
): Promise<OptTuple<any, Error>> => {
  return window.ipc?.invoke('gut-reset', projectKey, paths);
};

/* const gutGetProjFile = (
  projectKey: string,
  path: string,
): Promise<File> => {
  return window.ipc?.invoke('gut-getProjFile', projectKey, path);
}; */

const gutHashFile = (projectKey: string, path: string): Promise<string> => {
  return window.ipc?.invoke('gut-hashFile', projectKey, path);
};

const gutUploadFiles = (
  projectKey: string,
  uploads: any,
): Promise<OptTuple<string[], Error>> => {
  return window.ipc?.invoke('upload-files', projectKey, uploads);
};

const gutGetAllConnectedProjects = (): Promise<
  OptTuple<Record<string, string>, /* SyncedError */ any>
> => {
  return window.ipc?.invoke('gut-getAllConnectedProjects');
};

const listenForGutStatus = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-gut-status';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenForInbox = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-inbox';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenForPlusIcon = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-plus';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenForNotification = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-notification';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenForDownload = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-gut-download';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenForUpload = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-gut-upload';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenForFocus = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-focus-gained';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const listenNavigation = (handler: (event: any, ...args: any) => void) => {
  const channelName = 'subscribe-navigation';
  window.ipc?.receiveMessage(channelName, handler);
  return () => {
    window.ipc?.disconnect(channelName);
  };
};

const menuPopup = () => {
  window.ipc.invoke('menu-popup');
};

const windowOperation = (
  type: 'minimize' | 'maxUnmax' | 'close' | 'isWindowMaximized',
) => {
  return window.ipc.invoke('window', type);
};

const openElectronNotification = (data: {
  title: string;
  body: string;
  navigationTo: {
    project: {
      spaceType: string;
      prefix: '@' | '+';
      spaceSlug: string;
      projectSlug: string;
    };
  };
}): Promise<void> => {
  return window.ipc?.invoke('electron-notification', data);
};

const isInDesktopMode = () => {
  return window.appConfig?.isInDesktopMode || false;
};

const getPlatformFromApp = () => {
  if (!window.appConfig?.getPlatform) {
    return null;
  }

  return window.appConfig.getPlatform();
};

const createTab = (
  url: string,
  title: string,
  icon:
    | 'project'
    | 'file'
    | 'comparison'
    | 'rfq'
    | 'quote'
    | 'order'
    | 'project-file',
  navigation?: { routeName: string; options: any },
  option?: { uniqueFlag?: string },
) => {
  window.ipc?.tab?.({
    type: 'createTabOnWindow',
    data: {
      url,
      args: {
        title,
        icon,
      },
      navigation,
      ...(option || {}),
    },
  });
};

const getCurrentTabIdFromDesktop = async () => {
  return window.ipc?.invoke('current-tab-id');
};

const getHomeTabIdFromDesktop = async () => {
  return window.ipc?.invoke('home-tab-id');
};

const socialAction = async (payload: {
  type: 'inbox' | 'notification';
  action: 'open' | 'close';
}) => {
  window.ipc?.tab?.({
    type: 'socialAction',
    data: payload,
  });
};

const setNotificationCount = (count: number, tabId: number) => {
  window.ipc?.tab?.({
    type: 'setNotificationCount',
    data: {
      count,
      tabId,
    },
  });
};

const openUrlInBrowser = (url: string) => {
  window.ipc.invoke('shell-openExternalLink', url);
};

const setCurrentTabHoverTitle = (title: string) => {
  window.ipc?.tab?.({
    type: 'setCurrentTabHoverTitle',
    data: {
      title,
    },
  });
};

const switchToHomeTab = (navigation?: { routeName: string; options: any }) => {
  window.ipc?.tab?.({
    type: 'switchToHomeTab',
    data: { navigation },
  });
};

const closeTabById = (data: { id: number; needNotifyView: boolean }) => {
  window.ipc?.tab?.({
    data,
    type: 'closeTabOnTabPage',
  });
};

export {
  closeTabById,
  createTab,
  socialAction,
  gutIsConnected,
  gutConnect,
  gutDisconnect,
  gutFetch,
  gutMerge,
  gutSyncBlobs,
  gutPull,
  gutHeadHash,
  gutStatus,
  gutReadCommit,
  gutHashFile,
  gutUploadFiles,
  gutResolve,
  gutReset,
  gutResetProject,
  menuPopup,
  gutGetConnectedPath,
  gutGetAllConnectedProjects,
  listenForGutStatus,
  listenForDownload,
  listenForUpload,
  listenForFocus,
  listenNavigation,
  listenForInbox,
  listenForPlusIcon,
  listenForNotification,
  openElectronNotification,
  isInDesktopMode,
  getPlatformFromApp,
  getCurrentTabIdFromDesktop,
  setCurrentTabHoverTitle,
  getHomeTabIdFromDesktop,
  setNotificationCount,
  switchToHomeTab,
  openUrlInBrowser,
  windowOperation,
};
